import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  NoSymbolIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface Employee {
  id: number
  firstName: string
  lastName: string
  employeeNumber: string
  department: string
  position: string
}

interface LeaveType {
  id: number
  name: string
  code: string
  isPaid: boolean
}

interface Approver {
  id: number
  firstName: string
  lastName: string
}

interface LeaveRequest {
  id: number
  startDate: string
  endDate: string
  daysRequested: number
  reason: string | null
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  createdAt: string
  approvedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  employee: Employee
  leaveType: LeaveType
  approver: Approver | null
  rejecter: Approver | null
}

interface LeaveRequestsResponse {
  leaveRequests: LeaveRequest[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

interface LeaveRequestsListProps {
  onRefresh?: () => void
}

const LeaveRequestsList: React.FC<LeaveRequestsListProps> = ({ onRefresh }) => {
  const { user } = useAuth()
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [expandedRequest, setExpandedRequest] = useState<number | null>(null)
  const [approvalModal, setApprovalModal] = useState<{
    request: LeaveRequest | null
    action: 'approve' | 'reject' | null
  }>({ request: null, action: null })
  const [rejectionReason, setRejectionReason] = useState('')

  const queryClient = useQueryClient()

  // Get leave requests based on user role
  const { data: requestsData, isLoading } = useQuery<LeaveRequestsResponse>({
    queryKey: ['leaveRequests', selectedStatus],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus)
      }
      
      const response = await api.get(`/leaves/requests?${params.toString()}`)
      return response.data.data
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  })

  // Approve/Reject leave request mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ requestId, status, rejectionReason }: {
      requestId: number
      status: 'approved' | 'rejected'
      rejectionReason?: string
    }) => {
      const response = await api.put(`/leaves/requests/${requestId}/status`, {
        status,
        rejectionReason: status === 'rejected' ? rejectionReason : undefined
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      toast.success(`Leave request ${variables.status} successfully`)
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      queryClient.invalidateQueries({ queryKey: ['leaveBalances'] })
      setApprovalModal({ request: null, action: null })
      setRejectionReason('')
      onRefresh?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update leave request')
    }
  })

  // Cancel leave request mutation (for employees)
  const cancelMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await api.put(`/leaves/requests/${requestId}/cancel`)
      return response.data
    },
    onSuccess: () => {
      toast.success('Leave request cancelled successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      queryClient.invalidateQueries({ queryKey: ['leaveBalances'] })
      onRefresh?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to cancel leave request')
    }
  })

  const getStatusBadge = (status: LeaveRequest['status']) => {
    const configs = {
      pending: { 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200', 
        icon: ClockIcon, 
        label: 'Pending' 
      },
      approved: { 
        color: 'bg-green-50 text-green-700 border-green-200', 
        icon: CheckCircleIcon, 
        label: 'Approved' 
      },
      rejected: { 
        color: 'bg-red-50 text-red-700 border-red-200', 
        icon: XCircleIcon, 
        label: 'Rejected' 
      },
      cancelled: { 
        color: 'bg-gray-50 text-gray-700 border-gray-200', 
        icon: NoSymbolIcon, 
        label: 'Cancelled' 
      }
    }

    const config = configs[status]
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const canApproveReject = (request: LeaveRequest) => {
    if (request.status !== 'pending') return false
    
    // Admin and HR managers can approve all requests
    if (['admin', 'hr_manager'].includes(user?.role || '')) return true
    
    // Managers can approve requests from their department
    if (user?.role === 'manager' && user?.employee?.department === request.employee.department) {
      return true
    }
    
    return false
  }

  const canCancel = (request: LeaveRequest) => {
    return request.status === 'pending' && 
           user?.role === 'employee' && 
           request.employee.id === user?.employee?.id
  }

  const handleApprovalAction = (request: LeaveRequest, action: 'approve' | 'reject') => {
    setApprovalModal({ request, action })
    if (action === 'approve') {
      // Auto-approve without reason
      updateStatusMutation.mutate({
        requestId: request.id,
        status: 'approved'
      })
    }
  }

  const handleRejectSubmit = () => {
    if (!approvalModal.request || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason')
      return
    }

    updateStatusMutation.mutate({
      requestId: approvalModal.request.id,
      status: 'rejected',
      rejectionReason: rejectionReason.trim()
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          <p className="mt-2 text-sm font-medium text-gray-900">Loading leave requests...</p>
          <p className="text-xs text-gray-500">Fetching your requests</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-700">Filter by:</span>
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="rounded-lg border-gray-300 text-xs focus:border-blue-500 focus:ring-blue-500 py-2 px-3 border shadow-sm"
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="mt-3 lg:mt-0">
            <span className="text-xs text-gray-600">
              Total: <span className="font-semibold text-gray-900">{requestsData?.pagination.total || 0}</span> requests
            </span>
          </div>
        </div>
      </div>

      {/* Requests List */}
      {!requestsData?.leaveRequests.length ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-3 text-base font-medium text-gray-900">No leave requests found</h3>
          <p className="mt-1 text-xs text-gray-600 max-w-sm mx-auto">
            {selectedStatus === 'all' 
              ? 'No leave requests have been submitted yet.'
              : `No ${selectedStatus} leave requests found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requestsData.leaveRequests.map((request) => (
            <div 
              key={request.id} 
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 transition-all hover:shadow-md"
            >
              {/* Request Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                    {request.employee.firstName[0]}{request.employee.lastName[0]}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {user?.role === 'employee' ? request.leaveType.name : 
                         `${request.employee.firstName} ${request.employee.lastName}`}
                      </h3>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-600">
                      <span className="font-medium">{request.leaveType.name} ({request.leaveType.code})</span>
                      {user?.role !== 'employee' && (
                        <>
                          <span>•</span>
                          <span>{request.employee.department}</span>
                          <span>•</span>
                          <span>#{request.employee.employeeNumber}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setExpandedRequest(
                      expandedRequest === request.id ? null : request.id
                    )}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {expandedRequest === request.id ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Request Summary */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <CalendarDaysIcon className="h-4 w-4 text-blue-600 mr-2" />
                  <div>
                    <p className="text-xs font-medium text-gray-900">Date Range</p>
                    <p className="text-xs text-gray-600">
                      {formatDate(request.startDate)} - {formatDate(request.endDate)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <ClockIcon className="h-4 w-4 text-blue-600 mr-2" />
                  <div>
                    <p className="text-xs font-medium text-gray-900">Duration</p>
                    <p className="text-xs text-gray-600">
                      {request.daysRequested} day{request.daysRequested !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <UserIcon className="h-4 w-4 text-blue-600 mr-2" />
                  <div>
                    <p className="text-xs font-medium text-gray-900">Submitted</p>
                    <p className="text-xs text-gray-600">
                      {formatDate(request.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedRequest === request.id && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  {/* Reason */}
                  {request.reason && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-900 mb-2">Reason for Leave</h4>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-700 leading-relaxed">{request.reason}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Approval Info */}
                  {/* Approval Info */}
                  {(request.approver || request.rejecter) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {request.approver && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900 mb-2">Approved by</h4>
                          <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 bg-green-100 rounded flex items-center justify-center">
                              <UserIcon className="h-3 w-3 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-900">
                                {request.approver.firstName} {request.approver.lastName}
                              </p>
                              {request.approvedAt && (
                                <p className="text-xs text-gray-600">
                                  on {formatDate(request.approvedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {request.rejecter && (
                        <div>
                          <h4 className="text-xs font-semibold text-red-700 mb-2">Rejected by</h4>
                          <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 bg-red-100 rounded flex items-center justify-center">
                              <UserIcon className="h-3 w-3 text-red-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-red-900">
                                {request.rejecter.firstName} {request.rejecter.lastName}
                              </p>
                              {request.rejectedAt && (
                                <p className="text-xs text-red-600">
                                  on {formatDate(request.rejectedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Rejection Reason */}
                  {request.rejectionReason && (
                    <div>
                      <h4 className="text-xs font-semibold text-red-700 mb-2">Rejection Reason</h4>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-xs text-red-700 leading-relaxed">{request.rejectionReason}</p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {(canApproveReject(request) || canCancel(request)) && (
                    <div className="flex flex-wrap gap-2 pt-3">
                      {canApproveReject(request) && (
                        <>
                          <button
                            onClick={() => handleApprovalAction(request, 'approve')}
                            disabled={updateStatusMutation.isPending}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors shadow-sm"
                          >
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleApprovalAction(request, 'reject')}
                            disabled={updateStatusMutation.isPending}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
                          >
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Reject
                          </button>
                        </>
                      )}
                      
                      {canCancel(request) && (
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to cancel this leave request?')) {
                              cancelMutation.mutate(request.id)
                            }
                          }}
                          disabled={cancelMutation.isPending}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm"
                        >
                          <NoSymbolIcon className="h-3 w-3 mr-1" />
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rejection Reason Modal */}
      {approvalModal.action === 'reject' && approvalModal.request && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Reject Leave Request
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Please provide a reason for rejection
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4">
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter the reason for rejecting this leave request..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none text-sm"
                rows={3}
                required
              />
            </div>

            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setApprovalModal({ request: null, action: null })
                  setRejectionReason('')
                }}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectionReason.trim() || updateStatusMutation.isPending}
                className="px-4 py-2 text-xs font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {updateStatusMutation.isPending ? 'Rejecting...' : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveRequestsList